import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Paper,
  IconButton,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  adminService,
  type TransactionalEmailAuditRow,
  type TransactionalEmailTemplateMeta,
} from '@/services/AdminService'
import { isoToLocalDay, localDayEndIso, localDayStartIso } from '@/utils/transactionalEmailAuditDateRange'

const PAGE_SIZE = 50

function formatShort(s: string, max: number): string {
  if (!s) return '—'
  return s.length <= max ? s : `${s.slice(0, max)}…`
}

function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - days)
  return d.toISOString()
}

export interface TransactionalEmailAuditPanelProps {
  onBack: () => void
}

export default function TransactionalEmailAuditPanel({ onBack }: TransactionalEmailAuditPanelProps) {
  const [categories, setCategories] = useState<string[]>([])
  const [templates, setTemplates] = useState<TransactionalEmailTemplateMeta[]>([])
  const [rows, setRows] = useState<TransactionalEmailAuditRow[]>([])
  const [total, setTotal] = useState(0)
  const [skip, setSkip] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [companyId, setCompanyId] = useState('')
  const [userId, setUserId] = useState('')
  const [category, setCategory] = useState('')
  const [templateName, setTemplateName] = useState('')
  const [status, setStatus] = useState<'success' | 'failed' | ''>('')
  /** YYYY-MM-DD for `<input type="date" />`; converted to ISO when calling the API. */
  const [fromDay, setFromDay] = useState('')
  const [toDay, setToDay] = useState('')
  const [q, setQ] = useState('')
  const [includeLegacyEmailLog, setIncludeLegacyEmailLog] = useState(false)
  const [includeDevLogOnly, setIncludeDevLogOnly] = useState(false)

  const loadMeta = useCallback(async () => {
    const meta = await adminService.getTransactionalEmailFilters()
    setCategories(meta.categories || [])
    setTemplates(meta.templates || [])
  }, [])

  const fetchRows = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const fromIso = fromDay ? localDayStartIso(fromDay) : ''
      const toIso = toDay ? localDayEndIso(toDay) : ''
      const res = await adminService.getTransactionalEmails({
        limit: PAGE_SIZE,
        skip,
        companyId: companyId.trim() || undefined,
        userId: userId.trim() || undefined,
        category: category || undefined,
        templateName: templateName || undefined,
        status: status || undefined,
        from: fromIso || undefined,
        to: toIso || undefined,
        q: q.trim() || undefined,
        includeLegacyEmailLog: includeLegacyEmailLog || undefined,
        includeDevLogOnly: includeDevLogOnly || undefined,
      })
      setRows(res.rows || [])
      setTotal(typeof res.total === 'number' ? res.total : 0)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load emails')
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [skip, companyId, userId, category, templateName, status, fromDay, toDay, q, includeLegacyEmailLog, includeDevLogOnly])

  useEffect(() => {
    loadMeta().catch((e) => setError(e instanceof Error ? e.message : 'Failed to load filters'))
  }, [loadMeta])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  const templatesForCategory = useMemo(() => {
    if (!category) return templates
    return templates.filter((t) => t.category === category)
  }, [templates, category])

  const applyPreset = (preset: '24h' | '7d' | '30d' | 'failed' | 'billing') => {
    if (preset === 'failed') {
      setStatus('failed')
      setFromDay('')
      setToDay('')
      setCategory('')
      setSkip(0)
      return
    }
    if (preset === 'billing') {
      setCategory('billing')
      setStatus('')
      setFromDay('')
      setToDay('')
      setSkip(0)
      return
    }
    const nowIso = new Date().toISOString()
    const days = preset === '24h' ? 1 : preset === '7d' ? 7 : 30
    const fromIso = isoDaysAgo(days)
    setFromDay(isoToLocalDay(fromIso))
    setToDay(isoToLocalDay(nowIso))
    setStatus('')
    setSkip(0)
  }

  const clearPresets = () => {
    setFromDay('')
    setToDay('')
    setStatus('')
    setCategory('')
    setTemplateName('')
    setSkip(0)
  }

  const pageCount = Math.ceil(total / PAGE_SIZE) || 1
  const pageIndex = Math.floor(skip / PAGE_SIZE) + 1

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: 2, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <IconButton onClick={onBack} aria-label="Back to admin menu" color="primary">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Transactional emails
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<RefreshIcon />} onClick={() => fetchRows()} disabled={loading} variant="outlined" size="small">
          Refresh
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary">
        By default this list shows only rows from the current server mailer (Mailgun SMTP path), not legacy EmailLog sources or dev console-only sends. Use the checkboxes below to widen the scope.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
          Quick filters
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip label="Last 24h" onClick={() => applyPreset('24h')} variant="outlined" />
          <Chip label="Last 7 days" onClick={() => applyPreset('7d')} variant="outlined" />
          <Chip label="Last 30 days" onClick={() => applyPreset('30d')} variant="outlined" />
          <Chip label="Failed only" onClick={() => applyPreset('failed')} variant="outlined" color="error" />
          <Chip label="Billing" onClick={() => applyPreset('billing')} variant="outlined" />
          <Chip label="Clear presets" onClick={clearPresets} variant="outlined" />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          <TextField
            label="Company ID"
            size="small"
            value={companyId}
            onChange={(e) => {
              setCompanyId(e.target.value)
              setSkip(0)
            }}
            fullWidth
          />
          <TextField
            label="User ID"
            size="small"
            value={userId}
            onChange={(e) => {
              setUserId(e.target.value)
              setSkip(0)
            }}
            fullWidth
          />
          <TextField
            label="Search (email / subject)"
            size="small"
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setSkip(0)
            }}
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value)
                setTemplateName('')
                setSkip(0)
              }}
            >
              <MenuItem value="">All</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Template</InputLabel>
            <Select
              label="Template"
              value={templateName}
              onChange={(e) => {
                setTemplateName(e.target.value)
                setSkip(0)
              }}
            >
              <MenuItem value="">All</MenuItem>
              {templatesForCategory.map((t) => (
                <MenuItem key={t.key} value={t.key}>
                  {t.key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as '' | 'success' | 'failed')
                setSkip(0)
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="From date"
            type="date"
            size="small"
            value={fromDay}
            onChange={(e) => {
              setFromDay(e.target.value)
              setSkip(0)
            }}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To date"
            type="date"
            size="small"
            value={toDay}
            onChange={(e) => {
              setToDay(e.target.value)
              setSkip(0)
            }}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Range uses your local timezone; the server receives ISO timestamps.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={includeLegacyEmailLog}
                onChange={(_, c) => {
                  setIncludeLegacyEmailLog(c)
                  setSkip(0)
                }}
              />
            }
            label={
              <Typography variant="body2" color="text.primary">
                Include legacy EmailLog (pre–transactional-server)
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={includeDevLogOnly}
                onChange={(_, c) => {
                  setIncludeDevLogOnly(c)
                  setSkip(0)
                }}
              />
            }
            label={
              <Typography variant="body2" color="text.primary">
                Include dev log-only sends (EMAIL_LOG_ONLY)
              </Typography>
            }
          />
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          {total} total · page {pageIndex} / {pageCount} · sorted newest first
        </Typography>
        <Button size="small" disabled={skip <= 0 || loading} onClick={() => setSkip((s) => Math.max(0, s - PAGE_SIZE))}>
          Previous
        </Button>
        <Button
          size="small"
          disabled={skip + PAGE_SIZE >= total || loading}
          onClick={() => setSkip((s) => s + PAGE_SIZE)}
        >
          Next
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 'min(70vh, 720px)' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: 'primary.main' }} />
          </Box>
        )}
        {!loading && (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Sent</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Transport</TableCell>
                <TableCell>Sent by</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No rows match these filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                      {r.sentAt ? new Date(r.sentAt).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>{r.status}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {r.auditTransport || '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', maxWidth: 120 }}>{formatShort(r.sentBy || '', 24)}</TableCell>
                    <TableCell>{r.category || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 140 }}>{formatShort(r.templateName, 32)}</TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>{formatShort(r.recipientEmail, 40)}</TableCell>
                    <TableCell sx={{ maxWidth: 100, fontSize: '0.75rem' }}>{formatShort(r.companyId || '', 12)}</TableCell>
                    <TableCell sx={{ maxWidth: 100, fontSize: '0.75rem' }}>{formatShort(r.userId || '', 12)}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>{formatShort(r.subject, 80)}</TableCell>
                    <TableCell sx={{ maxWidth: 200, color: 'error.main', fontSize: '0.75rem' }}>
                      {formatShort(r.error || '', 100)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  )
}
